import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, Link2, Check, ArrowLeft } from "lucide-react";
import { buildFleetHtml, buildRecruiterHtml, buildSetterHtml, PRINT_CSS, wrapHtml } from "./market/agreementBuilders";

const META = {
  fleet:     { label: "Fleet Client Agreement",           badge: "Fleet / Motor Carrier",      color: "#FF4D00" },
  recruiter: { label: "Independent Recruiter Agreement",  badge: "Full-Cycle Recruiter",        color: "#F5C842" },
  setter:    { label: "Independent Setter Agreement",     badge: "Appointment Setter",          color: "#FFA500" },
};

function getHtml(type) {
  if (type === "fleet")     return buildFleetHtml({});
  if (type === "recruiter") return buildRecruiterHtml({});
  if (type === "setter")    return buildSetterHtml({});
  return "<p style='padding:40px;font-family:Arial;color:#888'>Unknown agreement type.</p>";
}

export default function AgreementPreviewPage() {
  const { type } = useParams();
  const meta       = META[type] || META.fleet;
  const iframeRef  = useRef(null);
  const [copied, setCopied] = useState(false);

  const bodyHtml  = getHtml(type);
  const fullHtml  = wrapHtml(bodyHtml, meta.label);

  // Inject into iframe via srcdoc
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.srcdoc = fullHtml;
    }
  }, [fullHtml]);

  function handlePrint() {
    const iframe = iframeRef.current;
    if (!iframe) return;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", display: "flex", flexDirection: "column" }}>

      {/* ── Header bar ── */}
      <div style={{
        background: "#111", borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "0 24px", height: "52px", display: "flex", alignItems: "center",
        gap: "16px", flexShrink: 0, position: "sticky", top: 0, zIndex: 50,
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <div style={{ width: "24px", height: "24px", background: "#FF4D00", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "10px", fontWeight: 900, color: "#fff" }}>H</span>
          </div>
          <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF4D00" }}>Hylas Companion</span>
        </Link>

        <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.1)" }} />

        {/* Agreement name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: meta.color, margin: 0 }}>{meta.badge}</p>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#fff", margin: 0, lineHeight: 1.2 }}>{meta.label}</p>
        </div>

        <span style={{
          fontSize: "8px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase",
          padding: "3px 8px", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.4)",
          background: "transparent",
        }}>Preview Template</span>

        {/* Actions */}
        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
          <button
            data-testid="preview-copy-link-btn"
            onClick={handleCopyLink}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "7px 14px", background: copied ? "rgba(74,222,128,0.12)" : "rgba(245,200,66,0.08)",
              border: `1px solid ${copied ? "rgba(74,222,128,0.3)" : "rgba(245,200,66,0.25)"}`,
              color: copied ? "#4ADE80" : "#F5C842", fontSize: "9px", fontWeight: 700,
              letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer",
              transition: "all 0.2s",
            }}>
            {copied ? <Check size={12} /> : <Link2 size={12} />}
            {copied ? "Copied!" : "Copy Link"}
          </button>
          <button
            data-testid="preview-print-btn"
            onClick={handlePrint}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "7px 16px", background: "#FF4D00", border: "none",
              color: "#fff", fontSize: "9px", fontWeight: 800,
              letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer",
            }}>
            <Printer size={12} /> Print / Download
          </button>
        </div>
      </div>

      {/* ── Type switcher ── */}
      <div style={{ background: "#0d0d0d", borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "0 24px", display: "flex", gap: "0", flexShrink: 0 }}>
        {Object.entries(META).map(([key, m]) => (
          <Link
            key={key}
            to={`/agreement-preview/${key}`}
            data-testid={`preview-tab-${key}`}
            style={{
              padding: "10px 18px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.15em",
              textTransform: "uppercase", textDecoration: "none",
              color: type === key ? m.color : "rgba(255,255,255,0.25)",
              borderBottom: `2px solid ${type === key ? m.color : "transparent"}`,
              transition: "all 0.15s",
            }}>
            {m.badge.split(" / ")[0]}
          </Link>
        ))}
      </div>

      {/* ── Disclaimer banner ── */}
      <div style={{ background: "rgba(245,200,66,0.05)", borderBottom: "1px solid rgba(245,200,66,0.1)", padding: "7px 24px", flexShrink: 0 }}>
        <p style={{ fontSize: "10px", color: "rgba(245,200,66,0.6)", margin: 0, textAlign: "center" }}>
          This is a <strong style={{ color: "#F5C842" }}>read-only preview template</strong> — no personal data is filled in. Actual agreements are signed through the Hylas Market portal.
        </p>
      </div>

      {/* ── Agreement iframe ── */}
      <div style={{ flex: 1, padding: "24px", background: "#0A0A0A" }}>
        <div style={{
          maxWidth: "860px", margin: "0 auto",
          boxShadow: "0 4px 40px rgba(0,0,0,0.6)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}>
          <iframe
            ref={iframeRef}
            data-testid="agreement-preview-iframe"
            title={meta.label}
            style={{ width: "100%", height: "calc(100vh - 180px)", minHeight: "600px", border: "none", background: "#fff" }}
          />
        </div>
      </div>

    </div>
  );
}
